/**
 * Pair two tag series into scatter points BY TIME.
 *
 * A scatter point claims "when X was this, Y was that", so both values must describe
 * the same moment. The widget used to zip the series index-by-index, which pairs
 * unrelated moments as soon as the two tags log at different rates (or one has a
 * gap), and it turned a missing value into 0.
 *
 * Rule: walk the coarser series; pair each of its samples with the NEAREST unused
 * sample of the finer one, if that sample lies within half the coarser series' median
 * step. Bucketed series from the same window share timestamps and pair exactly; a
 * sample with no neighbour close enough is left out rather than bridged. Samples with
 * a missing or non-finite value never pair.
 */
import type { TimeseriesPoint } from "@/lib/widgets/types";

export interface PairedPoint {
  x: number;
  y: number;
  id: string;
  /** ISO timestamp of the coarser series' sample the pair is anchored on. */
  t: string;
}

interface Sample {
  ms: number;
  v: number;
  t: string;
}

function clean(series: readonly TimeseriesPoint[]): Sample[] {
  const out: Sample[] = [];
  for (const p of series) {
    const ms = Date.parse(p.t);
    if (!Number.isFinite(ms) || p.v == null || !Number.isFinite(p.v)) continue;
    out.push({ ms, v: p.v, t: p.t });
  }
  return out.sort((a, b) => a.ms - b.ms);
}

/** Median gap between consecutive samples; Infinity when there are fewer than 2. */
function medianStep(s: readonly Sample[]): number {
  if (s.length < 2) return Number.POSITIVE_INFINITY;
  const d = s.slice(1).map((p, i) => p.ms - s[i].ms).sort((a, b) => a - b);
  const mid = Math.floor(d.length / 2);
  return d.length % 2 ? d[mid] : (d[mid - 1] + d[mid]) / 2;
}

/** Index of the first sample with ms >= target. */
function lowerBound(s: readonly Sample[], target: number): number {
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (s[mid].ms < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export function pairSeriesByTime(
  xs: readonly TimeseriesPoint[],
  ys: readonly TimeseriesPoint[],
): PairedPoint[] {
  const a = clean(xs);
  const b = clean(ys);
  if (a.length === 0 || b.length === 0) return [];
  const stepA = medianStep(a);
  const stepB = medianStep(b);
  const step = Math.max(stepA, stepB);
  const tol = Number.isFinite(step) ? step / 2 : 0; // single samples pair only exactly
  const xIsCoarse = stepA >= stepB;
  const coarse = xIsCoarse ? a : b;
  const fine = xIsCoarse ? b : a;
  const used = new Uint8Array(fine.length);

  const out: PairedPoint[] = [];
  for (const c of coarse) {
    const at = lowerBound(fine, c.ms);
    let best = -1;
    let bestGap = Number.POSITIVE_INFINITY;
    // Nearest unused neighbour on each side (the earlier one wins a tie).
    for (let i = at - 1; i >= 0; i--) {
      if (used[i]) continue;
      const gap = c.ms - fine[i].ms;
      if (gap <= tol && gap < bestGap) {
        best = i;
        bestGap = gap;
      }
      break;
    }
    for (let i = at; i < fine.length; i++) {
      if (used[i]) continue;
      const gap = fine[i].ms - c.ms;
      if (gap <= tol && gap < bestGap) {
        best = i;
        bestGap = gap;
      }
      break;
    }
    if (best < 0) continue;
    used[best] = 1;
    const f = fine[best];
    out.push({
      x: xIsCoarse ? c.v : f.v,
      y: xIsCoarse ? f.v : c.v,
      id: String(out.length),
      t: c.t,
    });
  }
  return out;
}
